import {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";

const DEFAULT_SCROLL_SHADOW_RAMP_DISTANCE = 160;
const DEFAULT_SCROLL_SHADOW_OVERFLOW_TOLERANCE = 1;

interface ScrollShadowStrengths {
  topStrength: number;
  bottomStrength: number;
}

type ScrollShadowViewportProps = Omit<
  ComponentProps<"div">,
  "children" | "className" | "style" | "ref"
> & {
  [key: `data-${string}`]: string | undefined;
};

interface ScrollShadowProps extends Omit<ComponentProps<"div">, "children"> {
  children: ReactNode;
  viewportClassName?: string;
  contentClassName?: string;
  viewportProps?: ScrollShadowViewportProps;
  viewportRef?: Ref<HTMLDivElement>;
  rampDistance?: number;
  /**
   * `transparent` paints no surface in the viewport, so the host's own background shows
   * through; the edge hairline and tone shadow still mark a clipped edge.
   */
  colorScheme?: "default" | "sidebar" | "raised" | "subtle" | "transparent";
}

export function getScrollShadowStrengths({
  scrollTop,
  clientHeight,
  scrollHeight,
  rampDistance = DEFAULT_SCROLL_SHADOW_RAMP_DISTANCE,
  overflowTolerance = DEFAULT_SCROLL_SHADOW_OVERFLOW_TOLERANCE,
}: {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
  rampDistance?: number;
  overflowTolerance?: number;
}): ScrollShadowStrengths {
  const hiddenOverflow = scrollHeight - clientHeight;
  if (hiddenOverflow <= overflowTolerance) {
    return {
      topStrength: 0,
      bottomStrength: 0,
    };
  }

  const hiddenAbove = Math.max(0, scrollTop - overflowTolerance);
  const hiddenBelow = Math.max(0, hiddenOverflow - scrollTop - overflowTolerance);

  const toStrength = (hiddenAmount: number) =>
    Math.max(0, Math.min(hiddenAmount / rampDistance, 1));

  return {
    topStrength: toStrength(hiddenAbove),
    bottomStrength: toStrength(hiddenBelow),
  };
}

export function ScrollShadow({
  children,
  className,
  contentClassName,
  viewportClassName,
  viewportProps,
  viewportRef,
  rampDistance = DEFAULT_SCROLL_SHADOW_RAMP_DISTANCE,
  colorScheme = "default",
  style,
  ...props
}: ScrollShadowProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollContentRef = useRef<HTMLDivElement | null>(null);
  const [strengths, setStrengths] = useState<ScrollShadowStrengths>({
    topStrength: 0,
    bottomStrength: 0,
  });

  useEffect(() => {
    const scrollElement = scrollContainerRef.current;
    const contentElement = scrollContentRef.current;
    if (!scrollElement) return;

    const updateStrengths = () => {
      const nextStrengths = getScrollShadowStrengths({
        scrollTop: scrollElement.scrollTop,
        clientHeight: scrollElement.clientHeight,
        scrollHeight: scrollElement.scrollHeight,
        rampDistance,
      });

      setStrengths((previousStrengths) =>
        previousStrengths.topStrength === nextStrengths.topStrength &&
        previousStrengths.bottomStrength === nextStrengths.bottomStrength
          ? previousStrengths
          : nextStrengths
      );
    };

    updateStrengths();
    scrollElement.addEventListener("scroll", updateStrengths, { passive: true });

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            updateStrengths();
          });

    resizeObserver?.observe(scrollElement);
    if (contentElement) {
      resizeObserver?.observe(contentElement);
    }

    return () => {
      scrollElement.removeEventListener("scroll", updateStrengths);
      resizeObserver?.disconnect();
    };
  }, [rampDistance]);

  // Memoised so the forwarded ref is attached once per viewport node, not detached and re-attached
  // on every render: a consumer's callback ref may install real listeners.
  const forwardViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      if (!viewportRef) {
        return;
      }
      if (typeof viewportRef === "function") {
        viewportRef(node);
        return;
      }
      viewportRef.current = node;
    },
    [viewportRef]
  );

  const scrollShadowStyle = useMemo(
    () =>
      ({
        ...style,
        "--scroll-shadow-ramp-distance": `${rampDistance}px`,
        "--scroll-shadow-top-strength": strengths.topStrength,
        "--scroll-shadow-bottom-strength": strengths.bottomStrength,
      }) as CSSProperties,
    [rampDistance, strengths.bottomStrength, strengths.topStrength, style]
  );

  return (
    <div
      className={cn("scroll-shadow-region relative flex min-h-0 flex-1 flex-col", className)}
      data-scroll-shadow-bottom-visible={strengths.bottomStrength > 0 ? "true" : "false"}
      data-scroll-shadow-scheme={colorScheme}
      data-scroll-shadow-top-visible={strengths.topStrength > 0 ? "true" : "false"}
      style={scrollShadowStyle}
      {...props}
    >
      <div
        ref={forwardViewportRef}
        className={cn(
          "scroll-shadow-viewport no-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto",
          viewportClassName
        )}
        {...viewportProps}
      >
        <div ref={scrollContentRef} className={contentClassName}>
          {children}
        </div>
      </div>
      <div
        aria-hidden="true"
        className="scroll-shadow-overlay scroll-shadow-overlay--top"
        style={{ opacity: strengths.topStrength }}
      />
      <div
        aria-hidden="true"
        className="scroll-shadow-overlay scroll-shadow-overlay--bottom"
        style={{ opacity: strengths.bottomStrength }}
      />
    </div>
  );
}
